import { useState, useEffect, createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { assistantApi, AssistantPrefs } from '../api/assistant';
import AssistantWidget from './AssistantWidget';
// Voice Mode 2.0 — P0 "Visible Spine" (feature-flagged, DEFAULT OFF server-side)
import { useUiControlBootstrap, useUiControlEvents } from '../lib/uiControl/useUiControlEvents';
import UiControlLayer from '../lib/uiControl/UiControlLayer';
import { useScreenReport } from '../lib/uiControl/screenReport';
import { AgentSessionProvider } from '../lib/agentSession';

interface VAContextValue {
  prefs: AssistantPrefs | null;
  refresh: () => Promise<void>;
}

const VAContext = createContext<VAContextValue>({ prefs: null, refresh: async () => {} });

export const useVirtualAssistant = () => useContext(VAContext);

export default function VirtualAssistantProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<AssistantPrefs | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // SSE UI control bus + overlay (no-op while the server flag is off)
  useUiControlBootstrap({ navigate });
  // Tell the server which controls are on screen, so the agent can address them.
  useScreenReport(location.pathname, Boolean(prefs?.enabled));

  const refresh = async () => {
    try {
      const p = await assistantApi.getPrefs();
      setPrefs(p);
    } catch {
      setPrefs(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('saik_token');
    if (token) refresh();
  }, []);

  // The RingOverlay may enable the widget when the user accepts a call while it
  // was off (owner rule 2026-10-13). Refresh prefs so the widget mounts at once.
  useEffect(() => {
    const onRefresh = () => { void refresh(); };
    window.addEventListener('saik:refresh-prefs', onRefresh);
    return () => window.removeEventListener('saik:refresh-prefs', onRefresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Publish the widget-enabled state globally so the always-mounted RingOverlay
  // can tell whether the widget is available for a call (owner rule 2026-10-13).
  useEffect(() => {
    try {
      document.documentElement.dataset.saikWidgetEnabled = prefs?.enabled ? '1' : '0';
      window.dispatchEvent(new CustomEvent('saik:widget-enabled', { detail: { enabled: Boolean(prefs?.enabled) } }));
    } catch { /* ignore */ }
    return () => { try { document.documentElement.dataset.saikWidgetEnabled = '0'; } catch { /* ignore */ } };
  }, [prefs?.enabled]);

  return (
    <AgentSessionProvider>
      <VAContext.Provider value={{ prefs, refresh }}>
      {children}
      <UiControlLayer />
      {prefs?.enabled && (
        <AssistantWidget
          assistantName={prefs.assistant_name}
          personalityMode={prefs.personality_mode}
          avatarId={prefs.avatar_id}
          customAvatarPath={prefs.custom_avatar_path}
          soundEnabled={prefs.sound_enabled}
          animationEnabled={prefs.animation_enabled}
          bubbleModeEnabled={prefs.bubble_mode_enabled}
          avatarIdRicardo={prefs.avatar_id_ricardo}
          avatarIdHenry={prefs.avatar_id_henry}
        />
      )}
      </VAContext.Provider>
    </AgentSessionProvider>
  );
}
